import React from 'react';
import { Text } from '@ayble-health/dashboard-components';
import { Badge } from 'components/ui/badge';
import { PmStatus } from '../../api/types';

enum ConfirmationStatus {
  confirmed = 'Confirmed',
  unconfirmed = 'Unconfirmed',
}

// Union of two enums because PmStatus does not cover the confirmation state,
// which comes from another property within the meeting object.
type Status = PmStatus & ConfirmationStatus;

// Bg + text now reference brand tokens (were inline off-token hex). Most
// were exact matches (aqua/steel/orange/squash/seafoam); the no-show maroon
// (#5D1505) had no brand equivalent and aligns to the nearest red, tomato.
const STATUS_BG_COLOR_MAP: Record<Status, string> = {
  [ConfirmationStatus.confirmed]: 'aqua',
  [ConfirmationStatus.unconfirmed]: 'steel',
  [PmStatus.cancelled]: 'orange',
  [PmStatus.lateCancelled]: 'squash',
  [PmStatus.noShow]: 'tomato',
};

const STATUS_TXT_COLOR_MAP: Record<Status, string> = {
  [ConfirmationStatus.confirmed]: 'white',
  [ConfirmationStatus.unconfirmed]: 'seafoam',
  [PmStatus.cancelled]: 'seafoam',
  [PmStatus.lateCancelled]: 'white',
  [PmStatus.noShow]: 'white',
};

type Props = {
  isConfirmed: boolean;
  pmStatus: PmStatus | undefined;
};

/**
 * @deprecated Superseded by `StatusPill` (ProviderHome), the single source
 * of truth for status badges. Retained for existing patient-profile call
 * sites; new usages and migrations should render `StatusPill` instead.
 * See the design-system audit → Archive.
 *
 * Renders a meeting's confirmation / `PmStatus` as a colored pill.
 * `pmStatus` wins if both are passed; falls back to confirmed /
 * unconfirmed when no explicit status is set. Colors are brand tokens.
 *
 * @example
 * <MeetingStatusBadge isConfirmed pmStatus={undefined} />
 *
 * @example
 * <MeetingStatusBadge isConfirmed pmStatus={PmStatus.lateCancelled} />
 */
export const MeetingStatusBadge = ({ isConfirmed, pmStatus }: Props) => {
  const meetingStatus =
    pmStatus ??
    (isConfirmed
      ? ConfirmationStatus.confirmed
      : ConfirmationStatus.unconfirmed);

  return (
    <Badge
      backgroundColor={STATUS_BG_COLOR_MAP[meetingStatus]}
      rounded={'xl'}
      h={'20px'}
      mr={1}
    >
      <Text color={STATUS_TXT_COLOR_MAP[meetingStatus]} fontSize={'xs'}>
        {meetingStatus}
      </Text>
    </Badge>
  );
};
